import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, unwrap } from '@/api/client';
import { organizationsListQuery, qk } from '@/api/queries';
import type { AuditEntry, User } from '@/api/types';
import { useSession } from '@/app/session';
import { DropdownChip, StatusDot } from '@/design-system';
import { now } from '@/lib/clock';
import { cx } from '@/lib/cx';
import { formatDateShort, formatTime, shortName } from '@/lib/format';
import { useBreadcrumbs } from '@/shell/breadcrumbs';
import { LoadError } from '@/shell/LoadError';
import { NoAccess, PageHeader } from '@/shell/PageHeader';
import { actorOf, dayLabel, OUTCOME_TONE, outcomeOf, summaryOf } from './format';
import s from './audit.module.css';

type Period = '' | 'today' | 'week' | 'month';
type Filters = { actor: string; action: string; period: Period; organization: string };

/** The start of the period, as the API's date_from (Tashkent days). */
function dateFrom(period: Period): string | undefined {
  if (!period) return undefined;
  const d = now();
  const days = period === 'today' ? 0 : period === 'week' ? 6 : 29;
  const day = new Date(d.getTime() - days * 86400000);
  const ymd = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tashkent' }).format(day);
  return new Date(`${ymd}T00:00:00+05:00`).toISOString();
}

// The actor filter's two non-user entries (P1-G01): rows written by the AI or the system.
const KIND_ACTORS = { 'kind:ai': 'ai', 'kind:system': 'system' } as const;
type KindActor = keyof typeof KIND_ACTORS;
const isKind = (v: string): v is KindActor => v in KIND_ACTORS;

/** «03.11.26 15:35», the time cell's tooltip. */
const formatDateTime = (at: string) => `${formatDateShort(at)} ${formatTime(at)}`;

const auditQuery = (f: Filters) => {
  const query = {
    size: 100,
    ...(f.actor && isKind(f.actor) ? { actor_kind: KIND_ACTORS[f.actor] } : f.actor ? { actor: f.actor } : {}),
    ...(f.action ? { action: f.action } : {}),
    ...(f.organization ? { organization: f.organization } : {}),
    ...(f.period ? { date_from: dateFrom(f.period) } : {}),
  };
  return {
    queryKey: qk.audit({ ...f }),
    queryFn: () => unwrap(api.GET('/admin/audit-logs', { params: { query } })),
    select: (p: { items: AuditEntry[] }) => p.items,
    placeholderData: keepPreviousData,
  };
};

/** /audit — Admin only; a PM sees NoAccess under the same breadcrumbs. */
export function AuditRoute() {
  const { t } = useTranslation('audit');
  const { isAdmin } = useSession();
  useBreadcrumbs([{ label: t('title') }]);
  return isAdmin ? <AuditPage /> : <NoAccess />;
}

function AuditPage() {
  const { t } = useTranslation('audit');
  const [f, setF] = useState<Filters>({ actor: '', action: '', period: '', organization: '' });
  const q = useQuery(auditQuery(f));
  // The option lists: the unfiltered page (actions seen), the staff, the organizations.
  const all = useQuery(auditQuery({ actor: '', action: '', period: '', organization: '' })).data;
  const users = useQuery({
    queryKey: [...qk.users, 'audit'],
    queryFn: () => unwrap(api.GET('/admin/users', { params: { query: { size: 100 } } })),
    select: (p: { items: User[] }) => p.items,
  }).data;
  const orgs = useQuery(organizationsListQuery).data;
  const actions = useMemo(() => [...new Set((all ?? []).map((e) => e.action))].sort(), [all]);
  const filtered = !!(f.actor || f.action || f.period || f.organization);
  const multiDay = useMemo(() => new Set((q.data ?? []).map((e) => formatDateShort(e.created_at))).size > 1, [q.data]);
  const set = <K extends keyof Filters>(k: K) => (v: Filters[K]) => setF((x) => ({ ...x, [k]: v }));

  const who = isKind(f.actor) ? t(f.actor === 'kind:ai' ? 'ai_actor' : 'system_actor') : users?.find((u) => u.id === f.actor)?.full_name;
  const where = orgs?.find((o) => o.id === f.organization)?.brand_name;
  // Filtering is additive (P1-F04): the chips sit in the header row, so the list keeps the design's place.
  const filters = (
    <div className={s.filters} role="group" aria-label={t('filters_label')}>
      <DropdownChip
        label={t('who', { who: who ? (isKind(f.actor) ? who : shortName(who)) : t('all') })}
        menuLabel={t('who_menu')}
        value={f.actor}
        onChange={set('actor')}
        options={[
          { value: '', label: t('all') },
          ...(users ?? []).map((u) => ({ value: u.id, label: u.full_name })),
          { value: 'kind:ai', label: t('ai_actor') },
          { value: 'kind:system', label: t('system_actor') },
        ]}
      />
      <DropdownChip
        label={t('what', { what: f.action || t('all') })}
        menuLabel={t('what_menu')}
        value={f.action}
        onChange={set('action')}
        options={[{ value: '', label: t('all') }, ...actions.map((a) => ({ value: a, label: a }))]}
      />
      <DropdownChip
        label={t('when', { when: f.period ? t(`periods.${f.period}`) : t('all_time') })}
        menuLabel={t('when_menu')}
        value={f.period}
        onChange={set('period')}
        options={[{ value: '' as Period, label: t('all_time') }, ...(['today', 'week', 'month'] as const).map((p) => ({ value: p, label: t(`periods.${p}`) }))]}
      />
      <DropdownChip
        label={t('where', { where: where ?? t('all') })}
        menuLabel={t('where_menu')}
        value={f.organization}
        onChange={set('organization')}
        options={[{ value: '', label: t('all') }, ...(orgs ?? []).map((o) => ({ value: o.id, label: o.brand_name }))]}
      />
    </div>
  );

  return (
    <>
      <PageHeader eyebrow={t('eyebrow')} title={t('title')} action={filters} />
      {q.isError ? (
        <LoadError error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        q.data && (
          <div className={s.card}>
            <div className={s.inner} role="table" aria-label={t('table')}>
              {q.data.map((e, i) => {
                const outcome = outcomeOf(e);
                const actor = actorOf(e, t);
                const summary = summaryOf(e);
                // A divider where the day changes, once the list spans more than today (the design draws one day).
                const day = formatDateShort(e.created_at);
                const divider = multiDay && (i === 0 || formatDateShort(q.data[i - 1]?.created_at ?? '') !== day);
                return (
                  <Fragment key={e.id}>
                    {divider && (
                      <div className={s.day} role="row">
                        <span role="cell">{dayLabel(e.created_at, now(), (k) => t(`days.${k}`))}</span>
                      </div>
                    )}
                    <div className={s.row} role="row">
                      <span className={s.time} role="cell" title={formatDateTime(e.created_at)}>
                        {formatTime(e.created_at)}
                      </span>
                      <span role="cell" aria-label={t(`outcome.${outcome}`)}>
                        <StatusDot tone={OUTCOME_TONE[outcome]} />
                      </span>
                      <span className={s.cell} role="cell" title={e.actor.label || actor}>
                        {actor}
                      </span>
                      <span className={cx(s.cell, s.action)} role="cell" title={`${e.entity} · ${e.action}`}>
                        {e.action}
                      </span>
                      <span className={cx(s.cell, s.summary)} role="cell" title={summary}>
                        {summary}
                      </span>
                    </div>
                  </Fragment>
                );
              })}
              {q.data.length === 0 && <div className={s.empty}>{filtered ? t('empty_filtered') : t('empty')}</div>}
            </div>
          </div>
        )
      )}
    </>
  );
}
